import { Component, OnInit } from '@angular/core';
import { FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { GlobalI18n } from '@settings/global-i18n';
import { Module } from '@shared/models/module-name';
import { MasterDataService } from '@shared/data-provider/master-data-api/api/masterData.service';
import { Airline } from '@shared/data-provider/master-data-api/model/airline';
import { Country } from '@shared/data-provider/master-data-api/model/country';
import { convertDateToUTC } from '@shared/functions/dateFunctions';
import { NzModalService } from 'ng-zorro-antd/modal';
import { map } from 'rxjs';
import { MasterDataFunctionsService } from '../../services/master-data.service';
import { CustomFormValidators } from '@shared/custom-form-validators';
import { SettingsService } from '@core/services/settings/settings.service';

interface ExtendedAirline extends Airline {
	nameEn?: string;
	nameRu?: string;
	nameLocal?: string;
	codeEn?: string;
	codeRu?: string;
	codeLocal?: string;
	winterWeightAdult?: number;
	summerWeightAdult?: number;
	winterWeightChild?: number;
	summerWeightChild?: number;
	winterWeightInfant?: number;
	summerWeightInfant?: number;
	dtRangeStart?: Date;
	dtRangeFinish?: Date;
}

@Component({
	selector: 'app-airlines',
	templateUrl: './airlines.component.html',
	styleUrls: ['./airlines.component.less'],
})
export class AirlinesComponent implements OnInit {
	constructor(
		private apiMasterData: MasterDataService,
		private settingsService: SettingsService,
		private modal: NzModalService,
		private fb: UntypedFormBuilder,
		private globalI18n: GlobalI18n,
		private functionsService: MasterDataFunctionsService
	) {}

	private formValidators: CustomFormValidators = new CustomFormValidators();

	private _airlines: ExtendedAirline[] = [];
	airline: ExtendedAirline;
	airlineForm: FormGroup;

	listOfDisplayCountries: { value: number; label: string }[] = [];

	settingValue = {
		loadingList: false,
		loadingModal: false,
		saving: false,
		searchIata: '',
		searchIcao: '',
		searchName: '',
		isVisibleModal: false,
		loadedPercent: null,
		pageIndex: 1,
	};

	listOfColumn = [];

	ngOnInit() {
		this.loadAircraftCountries();
		this.loadAirlines();
		this.listOfColumn = [
			{
				title: 'IATA',
				compare: (a: ExtendedAirline, b: ExtendedAirline) => a.iata.localeCompare(b.iata),
				priority: false,
			},
			{
				title: 'ICAO',
				compare: (a: ExtendedAirline, b: ExtendedAirline) => a.icao.localeCompare(b.icao),
				priority: false,
			},
			{
				title: this.globalI18n.getMessage(Module.MasterData, 'columnCode'),
				compare: (a: ExtendedAirline, b: ExtendedAirline) => a.codeLocal.localeCompare(b.codeLocal),
				priority: false,
			},
			{
				title: this.globalI18n.getMessage(Module.MasterData, 'columnName'),
				compare: (a: ExtendedAirline, b: ExtendedAirline) => a.nameLocal.localeCompare(b.nameLocal),
				priority: false,
			},
			{
				title: this.globalI18n.getMessage(Module.MasterData, 'columnLastUpdate'),
				compare: (a: ExtendedAirline, b: ExtendedAirline) => Date.parse(a.lastupdate) - Date.parse(b.lastupdate),
				priority: false,
			},
		];
	}

	loadAircraftCountries() {
		this.apiMasterData.selectRecords('countries').subscribe((data: Country[]) => {
			this.listOfDisplayCountries = data.map(item => ({
				value: item.id,
				label: this.settingsService.language == 'EN' ? item.name[0] : item.name[1],
			}));
		});
	}

	loadAirlines() {
		this.settingValue.loadingList = true;
		this.apiMasterData.selectRecords('airlines').subscribe((data: Airline[]) => {
			this._airlines = data.map(item => {
				return {
					...item,
					nameEn: item.name[0],
					nameRu: item.name[1],
					nameLocal: this.settingsService.language == 'EN' ? item.name[0] : item.name[1],
					codeEn: item.code[0],
					codeRu: item.code[1],
					codeLocal: this.settingsService.language == 'EN' ? item.code[0] : item.code[1],
					winterWeightAdult: item.weightAdult[0],
					summerWeightAdult: item.weightAdult[1],
					winterWeightChild: item.weightChild[0],
					summerWeightChild: item.weightChild[1],
					winterWeightInfant: item.weightInfant[0],
					summerWeightInfant: item.weightInfant[1],
					dtRangeStart: new Date(item.dtRange[0]),
					dtRangeFinish: new Date(item.dtRange[1]),
				};
			});
			this.settingValue.loadingList = false;
		});
	}

	get listOfDisplayAirlines(): ExtendedAirline[] {
		// При установленных фильтрах необходимо сбросить пагинацию на 1 страницу
		if (
			this.settingValue.searchIata.length !== 0 ||
			this.settingValue.searchIcao.length !== 0 ||
			this.settingValue.searchName.length !== 0
		) {
			this.onPageIndexChanged(1);
		}
		return this._airlines.filter(item => {
			return (
				item.iata.toLowerCase().indexOf(this.settingValue.searchIata.toLowerCase()) != -1 &&
				item.icao.toLowerCase().indexOf(this.settingValue.searchIcao.toLowerCase()) != -1 &&
				(item.nameRu.toLowerCase().indexOf(this.settingValue.searchName.toLowerCase()) != -1 ||
					item.nameEn.toLowerCase().indexOf(this.settingValue.searchName.toLowerCase()) != -1)
			);
		});
	}

	openAirline(id: number) {
		this.settingValue.loadingList = true;
		return this.apiMasterData
			.selectRecord('airlines', id.toString())
			.pipe(
				map((item: Airline) => {
					return {
						...item,
						nameEn: item.name[0],
						nameRu: item.name[1],
						nameLocal: this.settingsService.language == 'EN' ? item.name[0] : item.name[1],
						codeEn: item.code[0],
						codeRu: item.code[1],
						codeLocal: this.settingsService.language == 'EN' ? item.code[0] : item.code[1],
						winterWeightAdult: item.weightAdult[0],
						summerWeightAdult: item.weightAdult[1],
						winterWeightChild: item.weightChild[0],
						summerWeightChild: item.weightChild[1],
						winterWeightInfant: item.weightInfant[0],
						summerWeightInfant: item.weightInfant[1],
						countryId: item.countryId,
						dtRangeStart: new Date(item.dtRange[0]),
						dtRangeFinish: new Date(item.dtRange[1]),
					};
				})
			)
			.subscribe((airline: ExtendedAirline) => {
				this.airline = airline;
				this.airlineForm = this.createAirlineForm(airline);
				this.showModal();
				this.settingValue.loadingList = false;
			});
	}

	addType() {
		this.airline = {};
		this.airlineForm = this.createAirlineForm();
		this.showModal();
	}

	createAirlineForm(airline?: ExtendedAirline) {
		return this.fb.group(
			{
				iata: [
					airline ? airline.iata : '',
					[this.formValidators.onlyEnglishAndNumbers(), this.formValidators.lengthValidator(2)],
				],
				icao: [
					airline ? airline.icao : '',
					[this.formValidators.onlyEnglishAndNumbers(), this.formValidators.lengthValidator(3)],
				],
				nameEn: [airline ? airline.nameEn : '', [Validators.required, this.formValidators.onlyEnglish()]],
				nameRu: [airline ? airline.nameRu : '', [Validators.required]],
				codeEn: [airline ? airline.codeEn : '', [this.formValidators.onlyEnglish()]],
				codeRu: [airline ? airline.codeRu : ''],
				countryId: [airline ? airline.countryId : '', [Validators.required]],
				winterWeightAdult: [airline ? airline.winterWeightAdult : ''],
				summerWeightAdult: [airline ? airline.summerWeightAdult : ''],
				winterWeightChild: [airline ? airline.winterWeightChild : ''],
				summerWeightChild: [airline ? airline.summerWeightChild : ''],
				winterWeightInfant: [airline ? airline.winterWeightInfant : ''],
				summerWeightInfant: [airline ? airline.summerWeightInfant : ''],
				international: [airline ? airline.international : false],
				dtRangeStart: [airline ? convertDateToUTC(new Date(airline.dtRangeStart)) : new Date(), [Validators.required]],
				dtRangeFinish: [
					airline ? convertDateToUTC(new Date(airline.dtRangeFinish)) : new Date('2099-12-31 23:59'),
					[Validators.required],
				],
			},
			{
				validators: [this.formValidators.requiredFieldValidator(['iata', 'icao', 'codeRu'])],
			}
		);
	}

	add() {
		this.airlineForm.markAllAsTouched();
		if (this.airlineForm.valid) {
			this.settingValue.saving = true;
			this.apiMasterData.insertRecord('airlines', null, this.createFromForm()).subscribe({
				next: () => {
					this.closeModal();
					this.loadAirlines();
				},
				error: err => {
					this.displayError(err);
				},
				complete: () => {
					this.settingValue.saving = false;
				},
			});
		}
	}

	save() {
		this.airlineForm.markAllAsTouched();
		if (this.airlineForm.valid) {
			this.settingValue.saving = true;
			this.apiMasterData.updateRecord('airlines', this.airline.id.toString(), this.createFromForm()).subscribe({
				next: () => {
					this.closeModal();
					this.loadAirlines();
				},
				error: err => this.displayError(err),
				complete: () => {
					this.settingValue.saving = false;
				},
			});
		}
	}

	delete() {
		this.settingValue.saving = true;
		this.airlineForm.get('dtRangeFinish').setValue(new Date());
		this.apiMasterData.updateRecord('airlines', this.airline.id.toString(), this.createFromForm()).subscribe({
			next: () => {
				this.closeModal();
				this.loadAirlines();
			},
			error: err => this.displayError(err),
			complete: () => {
				this.settingValue.saving = false;
			},
		});
	}

	duplicate() {
		this.airline.id = undefined;
		this.airline.lastupdate = undefined;
	}

	showDeleteConfirm(): void {
		this.modal.confirm({
			nzTitle: this.globalI18n.getMessage(Module.MasterData, 'deleteItem'),
			nzOkText: this.globalI18n.getMessage(Module.MasterData, 'yes'),
			nzOkType: 'primary',
			nzOkDanger: true,
			nzOnOk: () => this.delete(),
			nzCancelText: this.globalI18n.getMessage(Module.MasterData, 'no'),
			nzOnCancel: () => console.log('Cancel'),
		});
	}

	createFromForm(): Airline {
		return {
			...this.airline,
			...this.airlineForm.value,
			name: [this.airlineForm.get('nameEn').value, this.airlineForm.get('nameRu').value],
			code: [this.airlineForm.get('codeEn').value, this.airlineForm.get('codeRu').value],
			weightAdult: [this.airlineForm.get('winterWeightAdult').value, this.airlineForm.get('summerWeightAdult').value],
			weightChild: [this.airlineForm.get('winterWeightChild').value, this.airlineForm.get('summerWeightChild').value],
			weightInfant: [
				this.airlineForm.get('winterWeightInfant').value,
				this.airlineForm.get('summerWeightInfant').value,
			],
			dtRange: [this.airlineForm.get('dtRangeStart').value, this.airlineForm.get('dtRangeFinish').value],
		};
	}

	downloadCsvFile() {
		this.functionsService.downloadCsvFile('airlines', this.settingValue.loadingList);
	}

	downloadXmlFile() {
		this.functionsService.downloadXmlFile('airlines', this.settingValue.loadingList);
	}

	onPageIndexChanged(index: number) {
		this.settingValue.pageIndex = index;
	}

	showModal(): void {
		this.settingValue.isVisibleModal = true;
	}

	closeModal(): void {
		this.settingValue.isVisibleModal = false;
	}

	displayError(err): void {
		this.modal.create({
			nzTitle: 'Error',
			nzContent: err.message,
			nzClosable: false,
		});
	}
}
